"use client";

import { useRef, useEffect, useCallback, useState } from "react";

/*
 * ClientReels — Cinematic dual-lane marquee
 *
 * Two rows of phone-framed video reels scroll in opposite directions,
 * creating a film-strip effect. The top row drifts left, the bottom row
 * drifts right. On hover over any phone, that video pauses its marquee
 * lane and scales up slightly. Click opens a full-screen lightbox.
 *
 * On mobile the two rows stack with different scroll speeds driven purely
 * by CSS animation — no JS scroll listeners, pure GPU performance.
 */

const REELS_A = [
  "/assets/stories/reel-3.mp4",
  "/assets/stories/reel-7.mp4",
  "/assets/stories/reel-1.mp4",
  "/assets/stories/reel-5.mp4",
];

const REELS_B = [
  "/assets/stories/reel-6.mp4",
  "/assets/stories/reel-2.mp4",
  "/assets/stories/reel-4.mp4",
  "/assets/stories/reel-7.mp4",
];

const LANE_A = [...REELS_A, ...REELS_A];
const LANE_B = [...REELS_B, ...REELS_B];

function ReelPhone({
  src,
  onOpen,
  timeOffset,
}: {
  src: string;
  onOpen: (src: string) => void;
  timeOffset: number;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const phoneRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [shouldLoad, setShouldLoad] = useState(false);
  const offsetApplied = useRef(false);

  useEffect(() => {
    const el = phoneRef.current;
    if (!el) return;

    const obs = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
        // Start loading the video source once it nears the viewport, and
        // keep it loaded thereafter (sticky) to avoid reload flicker.
        if (entry.isIntersecting) setShouldLoad(true);
      },
      // rootMargin preloads ~300px before the reel scrolls into view so the
      // marquee never shows a blank frame — behaviour stays identical.
      { threshold: 0.3, rootMargin: "300px 0px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (!offsetApplied.current) {
      const apply = () => {
        if (video.duration && isFinite(video.duration)) {
          video.currentTime = video.duration * timeOffset;
          offsetApplied.current = true;
        }
      };
      if (video.readyState >= 1) {
        apply();
      } else {
        video.addEventListener("loadedmetadata", apply, { once: true });
      }
    }

    if (isVisible) {
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  }, [isVisible, timeOffset]);

  return (
    <div
      className="iv-reels__phone"
      ref={phoneRef}
      onClick={() => onOpen(src)}
      role="button"
      tabIndex={0}
      aria-label={`Play wedding invitation reel ${
        src.match(/reel-(\d+)/)?.[1] ?? ""
      }`.trim()}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onOpen(src);
      }}
    >
      <div className="iv-reels__screen">
        {/* The marquee plays a muted 12s preview encode (~0.5 MB); the full
            reel with audio only loads when the lightbox opens. No autoPlay
            attribute — it overrides preload and force-downloads the file;
            playback is driven by the visibility effect above instead. */}
        <video
          ref={videoRef}
          src={shouldLoad ? src.replace(".mp4", "-preview.mp4") : undefined}
          poster={src.replace(".mp4", "-poster.webp")}
          className="iv-reels__video"
          muted
          loop
          playsInline
          preload="none"
        />
        {/* Play icon overlay */}
        <div className="iv-reels__play-hint">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <polygon points="8 5 20 12 8 19 8 5" />
          </svg>
        </div>
      </div>
    </div>
  );
}

function Lightbox({
  src,
  onClose,
}: {
  src: string;
  onClose: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEsc);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleEsc);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <div className="iv-reels__lightbox" onClick={onClose}>
      <div
        className="iv-reels__lightbox-content"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="iv-reels__lightbox-close"
          onClick={onClose}
          aria-label="Close"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
        <div className="iv-reels__lightbox-phone">
          <video
            ref={videoRef}
            src={src}
            className="iv-reels__lightbox-video"
            autoPlay
            playsInline
            loop
            controls
          />
        </div>
      </div>
    </div>
  );
}

export default function ClientReels() {
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const [isPaused, setIsPaused] = useState(false);

  const handleOpen = useCallback((src: string) => {
    setLightboxSrc(src);
  }, []);

  const handleClose = useCallback(() => {
    setLightboxSrc(null);
  }, []);

  return (
    <>
      <section
        className={`iv-reels ${isPaused ? "iv-reels--paused" : ""}`}
        id="client-reels"
        ref={sectionRef}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        {/* Section header */}
        <div className="iv-reels__header">
          <span className="iv-reels__eyebrow iv-font-script">
            Real Celebrations
          </span>
          <h2 className="iv-reels__title iv-font-display">
            Love Stories We Brought to Life
          </h2>
          <p className="iv-reels__subtitle">
            Watch how our couples shared their special day
          </p>
        </div>

        {/* Lane A — scrolls left */}
        <div className="iv-reels__lane iv-reels__lane--a">
          <div className="iv-reels__track">
            {LANE_A.map((src, i) => (
              <ReelPhone
                key={`a-${i}`}
                src={src}
                onOpen={handleOpen}
                timeOffset={(i * 0.13) % 1}
              />
            ))}
          </div>
        </div>

        {/* Lane B — scrolls right */}
        <div className="iv-reels__lane iv-reels__lane--b">
          <div className="iv-reels__track">
            {LANE_B.map((src, i) => (
              <ReelPhone
                key={`b-${i}`}
                src={src}
                onOpen={handleOpen}
                timeOffset={(i * 0.13 + 0.37) % 1}
              />
            ))}
          </div>
        </div>

        {/* Instagram CTA */}
        <div className="iv-reels__cta">
          <a
            href="https://www.instagram.com/myshaadilink"
            target="_blank"
            rel="noopener noreferrer"
            className="iv-reels__ig-btn"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" className="iv-reels__ig-icon">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
            </svg>
            Follow our journal on Instagram
          </a>
        </div>

        {/* Edge fades — warm cream gradient masks on left/right edges */}
        <div className="iv-reels__fade iv-reels__fade--left" />
        <div className="iv-reels__fade iv-reels__fade--right" />
      </section>

      {lightboxSrc && <Lightbox src={lightboxSrc} onClose={handleClose} />}
    </>
  );
}
